import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './InputCollection.translations.json';
import React, { useMemo } from 'react';
import { DashboardPanel } from '../DashboardPanel';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useListInputCollectionQuery } from './query/useGetInputCollectionQuery';
import { Table } from '../../common/tanstacktable';
import { InputCollectionType } from './schema/types';
import {
  formatDate,
  formatDateTimeShort,
  getDayjs,
  getDayjsNow,
} from '../../common/utils';
import { Progress } from 'antd';
import { colorAccent1 } from '@atlas-frontend/design-tokens';
import { InfoIconUrl, TextEllipsis } from '../../common/components';

const { addTranslations, t } = i18nNsFactory('input-collection');
addTranslations(translations);

const columnHelper = createColumnHelper<InputCollectionType>();
function getColumns(): ColumnDef<InputCollectionType, any>[] {
  const columns: ColumnDef<InputCollectionType, any>[] = [];
  columns.push(
    columnHelper.accessor('name', {
      header: t(`name.label`),
    }),
    columnHelper.accessor('valueDate', {
      header: t(`valueDate.label`),
      cell: (props) => formatDate(props.renderValue()),
    }),
    columnHelper.accessor('collectionRatio', {
      header: t(`collectionRatio.label`),
      cell: (props) => (
        <Progress percent={Math.round(props.renderValue() * 100)} />
      ),
      enableColumnFilter: false,
    }),
    columnHelper.accessor('dueDateTime', {
      header: t(`dueDateTime.label`),
      cell: (props) => {
        const { dueDateTime, collectionRatio } = props.row.original;
        const isWarning =
          dueDateTime &&
          getDayjs(dueDateTime)?.isBefore(getDayjsNow()) &&
          !collectionRatio;

        return (
          <TextEllipsis style={{ color: isWarning ? colorAccent1 : undefined }}>
            {formatDateTimeShort(props.row.original.dueDateTime)}
          </TextEllipsis>
        );
      },
      enableColumnFilter: false,
    }),
  );

  return columns;
}

export const InputCollection = () => {
  const { data: inputCollection = [], isFetching } =
    useListInputCollectionQuery();

  const columns: ColumnDef<InputCollectionType, any>[] = useMemo(
    () => getColumns(),
    [],
  );
  return (
    <DashboardPanel
      title={t('title')}
      infoIconUrl={InfoIconUrl.INPUT_COLLECTION}
      hideEmptyImage
    >
      <Table
        name="input-collection-table"
        columns={columns}
        data={inputCollection}
        hideClearFilters
        isLoading={isFetching}
        columnOrderingDisabled={true}
        columnResizeDisabled={true}
        hideEmptyImage
        sticky
        virtual
        scrollContainerSelector="#dashboard-panel-content"
      />
    </DashboardPanel>
  );
};
